import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import useActiveCondominio from './useActiveCondominio';

/**
 * useDadosCondominio - Hook global para gerenciar dados de blocos/apartamentos
 * 
 * Features:
 * - Cache em memória por condominioId (Map compartilhado entre todas instâncias)
 * - Invalidação manual via refetch()
 * - AbortController automático (evita race conditions)
 * - Reage automaticamente a mudanças no activeCondominio
 * - Multi-tenant safe (cache isolado por condomínio)
 * 
 * @param {Object} options - Opções de configuração
 * @param {string} options.projection - Projection de contexto (base.v1, reservatorios.v1, apartamentos.v1, exaustores.v1)
 * 
 * @returns {Object} { blocos, apartamentos, exaustoresPorBloco, loading, error, refetch }
 */

// Cache compartilhado entre todas as instâncias do hook
const cache = new Map();

const useDadosCondominio = ({
  projection = 'apartamentos.v1'
} = {}) => {
  const { activeCondominio } = useActiveCondominio();
  const [blocos, setBlocos] = useState([]);
  const [apartamentos, setApartamentos] = useState({});
  const [exaustoresPorBloco, setExaustoresPorBloco] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const abortControllerRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  /**
   * Buscar dados do condomínio (com cache)
   */
  const fetchDados = useCallback(async (condominioId, forceRefresh = false) => {
    if (!condominioId) {
      setBlocos([]);
      setApartamentos({});
      setExaustoresPorBloco({});
      return;
    }

    // Verificar cache (se não for refresh forçado)
    const cacheKey = `${condominioId}-${projection}`;
    if (!forceRefresh && cache.has(cacheKey)) {
      const cached = cache.get(cacheKey);
      setBlocos(cached.blocos);
      setApartamentos(cached.apartamentos || {});
      setExaustoresPorBloco(cached.exaustoresPorBloco || {});
      return;
    }

    // Cancelar requisição anterior
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const endpoint = `${apiUrl}/api/condominios/contexto?projection=${projection}`;
      const response = await axios.get(endpoint, {
        headers: {
          'x-condominio-id': condominioId
        },
        signal: abortControllerRef.current.signal
      });

      const {
        blocos: blocosDados,
        apartamentos: apartamentosDados,
        exaustoresPorBloco: exaustoresMetaDados,
      } = response.data.dados;

      // Atualizar state
      setBlocos(blocosDados || []);
      setApartamentos(apartamentosDados || {});
      setExaustoresPorBloco(exaustoresMetaDados || {});

      // Cachear resultado
      cache.set(cacheKey, {
        blocos: blocosDados || [],
        apartamentos: apartamentosDados || {},
        exaustoresPorBloco: exaustoresMetaDados || {},
      });

    } catch (err) {
      // Ignorar se foi cancelado (AbortError ou CanceledError do Axios)
      if (
        err.name === 'AbortError' || 
        err.name === 'CanceledError' ||
        err.code === 'ABORT_ERR' || 
        err.code === 'ERR_CANCELED'
      ) {
        return;
      }

      console.error('Erro ao buscar dados do condomínio:', err);
      setError('Erro ao carregar dados do condomínio.');
      
      // Em caso de erro, não cachear
      setBlocos([]);
      setApartamentos({});
      setExaustoresPorBloco({});
    } finally {
      setLoading(false);
    }
  }, [apiUrl, projection]);

  /**
   * Refetch manual (invalida cache)
   */
  const refetch = useCallback(() => {
    if (activeCondominio) {
      fetchDados(activeCondominio, true);
    }
  }, [activeCondominio, fetchDados]);

  /**
   * Limpar cache de um condomínio específico ou todos
   */
  const clearCache = useCallback((condominioId = null) => {
    if (condominioId) {
      // Limpar cache específico
      const keysToDelete = Array.from(cache.keys()).filter((key) => key.startsWith(`${condominioId}-`));
      keysToDelete.forEach((key) => cache.delete(key));
    } else {
      // Limpar todo cache
      cache.clear();
    }
  }, []);

  // Limpar cache automaticamente no logout (evento do auth)
  useEffect(() => {
    const handleAuthTokenChanged = () => {
      clearCache();
    };

    window.addEventListener('auth-token-changed', handleAuthTokenChanged);
    return () => {
      window.removeEventListener('auth-token-changed', handleAuthTokenChanged);
    };
  }, [clearCache]);

  /**
   * Limpar erro
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Reagir a mudanças no activeCondominio
  useEffect(() => {
    if (activeCondominio) {
      fetchDados(activeCondominio);
    }

    // Cleanup: cancelar requisição pendente ao desmontar
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [activeCondominio, fetchDados]);

  return {
    blocos,
    apartamentos,
    exaustoresPorBloco,
    loading,
    error,
    refetch,
    clearCache,
    clearError
  };
};

export default useDadosCondominio;
